import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import QuantityInput from '../QuantityInput';
import { toast } from 'sonner';
import axios from 'axios';

export default function BulkDiscountsModal({ isOpen, onClose, onUpdate }) {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchDiscounts();
    }
  }, [isOpen]);

  const fetchDiscounts = async () => {
    setLoading(true);
    try {
      console.log('🔄 Fetching bulk discounts...');
      const response = await axios.get('/admin/bulk-discounts');
      console.log('✅ Bulk discounts response:', response.data);
      const discountsList = response.data.discounts || [];
      console.log(`📊 Loaded ${discountsList.length} products`);
      setDiscounts(discountsList);
    } catch (error) {
      console.error('❌ Failed to fetch bulk discounts:', error);
      toast.error(`Failed to load bulk discounts: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put('/admin/bulk-discounts', discounts);
      toast.success('Bulk discounts updated successfully!');
      if (onUpdate) {
        onUpdate();
      }
      onClose();
    } catch (error) {
      console.error('Failed to save bulk discounts:', error);
      toast.error('Failed to save bulk discounts');
    } finally {
      setSaving(false);
    }
  };

  const updateDiscount = (index, field, value) => {
    const updated = [...discounts];
    updated[index][field] = value;
    setDiscounts(updated);
  };

  const formatPrice = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Bulk Discounts</DialogTitle>
          <DialogDescription>
            Set discount pricing when customers order in bulk. Discounts only apply when quantity threshold is met.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-gray-500">Loading discounts...</div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 font-semibold text-sm text-gray-700 border-b pb-2">
                <div className="col-span-1">SKU</div>
                <div className="col-span-3">Product Name</div>
                <div className="col-span-2">Regular Price</div>
                <div className="col-span-3">Discount Price (AUD)</div>
                <div className="col-span-3">Discount Quantity</div>
              </div>

              {/* Table Rows */}
              <div className="space-y-2">
                {discounts.map((discount, index) => (
                  <div
                    key={discount.sku}
                    className="grid grid-cols-12 gap-4 items-center py-3 border-b hover:bg-gray-50"
                  >
                    {/* SKU */}
                    <div className="col-span-1 text-sm font-medium text-gray-900">
                      {discount.sku}
                    </div>

                    {/* Product Name */}
                    <div className="col-span-3 text-sm text-gray-700">
                      {discount.name}
                    </div>

                    {/* Regular Price */}
                    <div className="col-span-2 text-sm text-gray-600">
                      {formatPrice(discount.price)}
                    </div>

                    {/* Discount Price Input */}
                    <div className="col-span-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={(discount.discount_price / 100).toFixed(2)}
                          onChange={(e) => {
                            const cents = Math.round(parseFloat(e.target.value || 0) * 100);
                            updateDiscount(index, 'discount_price', cents);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    {/* Discount Quantity */}
                    <div className="col-span-3">
                      <QuantityInput
                        value={discount.discount_quantity || 0}
                        onChange={(value) => updateDiscount(index, 'discount_quantity', value)}
                        min={0}
                        max={9999}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {discounts.length === 0 && !loading && (
                <div className="text-center py-8 text-gray-500">
                  No products found
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <div className="flex justify-between items-center w-full">
            <p className="text-sm text-gray-600">
              Set discount price and quantity to $0 to disable discount for a product
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-custom-orange hover:bg-custom-orange"
              >
                {saving ? 'Saving...' : 'Save Discounts'}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
